import type { Category } from '../types';
import { FACTS } from '../facts';

const tr = FACTS.trustedRunner;

export const buildingYourBook: Category = {
  slug: "building-your-book",
  half: "work",
  accent: '#343F5B',
  icon: "percent",
  label: { en: "Building your book", es: "Construye tu cartera" },
  blurb: {
    en: "Profile quality, prospect-board invites, capacity strategy — and what Trusted Runner status earns you.",
    es: "Calidad de tu perfil, invitaciones desde el tablero de prospectos, estrategia de capacidad — y lo que te da el estatus de Trusted Runner.",
  },
  guides: [
    {
      slug: "your-schedule-is-a-promise",
      related: [
        "building-your-book/capacity-and-trusted-runner",
        "safety-on-deliveries/severe-weather",
        "reporting-a-problem/what-never-counts-against-you",
      ],
      body: {
        en: {
          title: "Your schedule is a promise",
          summary:
            "You choose when you work. Once a delivery lands on a day you chose, a household is counting on it — this is the standard Grocery List Plus holds hardest.",
          sections: [
            {
              heading: "You set the schedule",
              paragraphs: [
                "Nobody assigns you hours. You pick your days, your delivery windows, your zones and your capacity, and you can change any of them going forward whenever you want. That freedom is the whole point of this model.",
                "It is also what makes the other half of the deal fair. You are not being told when to work. You are telling us — and telling your customers.",
              ],
            },
            {
              heading: "Once it is on your schedule, it is a commitment",
              paragraphs: [
                "When a delivery falls on a day you said you would work, a household has planned around it. Dinner that night. Formula. A parent who does not drive. They are not refreshing an app hoping someone accepts — they are expecting you, by name.",
                "So a delivery you accepted and then simply did not make is the one thing there is close to zero tolerance for here. Not a scoring nuance — it is the thing that breaks the product for the household who trusted it.",
              ],
            },
            {
              heading: "If you cannot make it, the clock is everything",
              paragraphs: [
                "Life happens, and nobody expects a perfect record. What matters is when we hear about it. Tell Grocery List Plus and tell your customer as early as you possibly can.",
              ],
              bullets: [
                "Days ahead: change your schedule yourself. Nothing is owed and nobody is affected.",
                "The day before: message us and your customer. We can move the window or cover the run.",
                "Same day: call support, do not just message. There is less time to cover it, so speed is the whole thing.",
                "Never: going quiet. A missed delivery with no word is the version that ends a Runner relationship.",
              ],
            },
            {
              heading: "What is not a broken promise",
              paragraphs: [
                "An accident. A situation at a door that is genuinely unsafe. A weather emergency that closes roads while you are already out. Getting sick. None of these are no-shows, and you will never be penalized for refusing to walk into danger.",
                "What is expected in those moments is the phone call, not the risk. Get safe first, then tell us immediately so we can re-route and let the household know. Safety is never traded; silence is never excused.",
              ],
            },
          ],
          actions: [
            "Open next week's schedule and cut any day you are not sure about — changing it in advance costs nothing.",
            "Save the support number in your phone so a same-day problem is one tap, not a search.",
            "If something is already shaky this week, tell us and your customer today, not the morning of.",
          ],
        },
        es: {
          title: "Tu horario es una promesa",
          summary:
            "Tú eliges cuándo trabajas. Una vez que una entrega cae en un día que elegiste, un hogar cuenta con ella — este es el estándar que Lista de Compras Plus sostiene con más firmeza.",
          sections: [
            {
              heading: "Tú armas el horario",
              paragraphs: [
                "Nadie te asigna horas. Tú eliges tus días, tus ventanas de entrega, tus zonas y tu capacidad, y puedes cambiar cualquiera de esas cosas hacia adelante cuando quieras. Esa libertad es el punto central de este modelo.",
                "También es lo que hace justa la otra mitad del trato. Nadie te dice cuándo trabajar. Tú nos lo dices a nosotros — y se lo dices a tus clientes.",
              ],
            },
            {
              heading: "Una vez en tu horario, es un compromiso",
              paragraphs: [
                "Cuando una entrega cae en un día que dijiste que trabajarías, un hogar ya planeó en torno a ella. La cena de esa noche. La fórmula del bebé. Una madre o un padre que no maneja. No están actualizando una app esperando que alguien acepte: te esperan a ti, por tu nombre.",
                "Por eso una entrega que aceptaste y simplemente no hiciste es lo único ante lo que aquí hay prácticamente cero tolerancia. No es un detalle de puntaje: es lo que rompe el producto para el hogar que confió en él.",
              ],
            },
            {
              heading: "Si no puedes, el reloj lo es todo",
              paragraphs: [
                "La vida pasa y nadie espera un récord perfecto. Lo que importa es cuándo nos enteramos. Avísale a Lista de Compras Plus y avísale a tu cliente lo antes que puedas.",
              ],
              bullets: [
                "Con días de anticipación: cambia tu horario tú mismo. No le debes nada a nadie y nadie se ve afectado.",
                "El día anterior: mándanos un mensaje a nosotros y a tu cliente. Podemos mover la ventana o cubrir el recorrido.",
                "El mismo día: llama a soporte, no solo mandes mensaje. Hay menos tiempo para cubrirlo, así que la rapidez lo es todo.",
                "Nunca: quedarte callado. Una entrega perdida sin avisar es la versión que termina una relación con un Runner.",
              ],
            },
            {
              heading: "Qué no es una promesa rota",
              paragraphs: [
                "Un accidente. Una situación en una puerta que de verdad no es segura. Una emergencia climática que cierra caminos cuando ya estás afuera. Enfermarte. Nada de eso es una falta, y nunca se te penalizará por negarte a meterte en peligro.",
                "Lo que se espera en esos momentos es la llamada, no el riesgo. Primero ponte a salvo y luego avísanos de inmediato para que podamos redirigir y avisarle al hogar. La seguridad nunca se negocia; el silencio nunca se excusa.",
              ],
            },
          ],
          actions: [
            "Abre el horario de la próxima semana y quita cualquier día del que no estés seguro: cambiarlo con anticipación no cuesta nada.",
            "Guarda el número de soporte en tu teléfono para que un problema del mismo día sea un toque, no una búsqueda.",
            "Si algo ya se ve inestable esta semana, avísanos a nosotros y a tu cliente hoy, no la mañana del recorrido.",
          ],
        },
      },
    },
    {
      slug: "your-profile",
      related: [
        "building-your-book/prospect-board-and-invites",
        "building-your-book/capacity-and-trusted-runner",
        "safety-on-deliveries/severe-weather",
      ],
      body: {
        en: {
          title: "Your profile",
          summary: "What customers see, what a human reviews before it goes live, and how to get it right the first time.",
          sections: [
            {
              heading: "Why it matters",
              paragraphs: [
                "Each customer has one primary Runner. When a household picks you, you are the person they expect at the door week after week. A clear, complete profile is how you win those repeat customers and keep them.",
              ],
            },
            {
              heading: "Your photo",
              paragraphs: [
                "Your public profile photo is reviewed by a human before it goes live. Every change is reviewed too, and the old photo stays live until the new one is approved, so nothing goes dark.",
              ],
              bullets: [
                "Head and shoulders, facing the camera.",
                "Good light. Daylight near a window works.",
                "No sunglasses.",
                "No other people in the frame.",
                "No text, logos, handles, or phone numbers in the image.",
              ],
            },
            {
              heading: "How you arrive",
              paragraphs: [
                "Your vehicle is described as \"how you arrive\": make, model, and color, plus an optional photo. That is so your customers recognize you when you pull up. Your licence plate is never shown, and Grocery List Plus does not rank Runners on their vehicle.",
              ],
            },
            {
              heading: "Languages",
              paragraphs: [
                "The languages you speak are shown to customers. List every one you are comfortable using at the door. A shared language is often the reason a household picks a Runner.",
              ],
            },
          ],
          actions: [
            "Take a fresh photo that meets all five rules and submit it for review.",
            "Fill in make, model, and color, and add a vehicle photo if you have a good one.",
            "Add every language you speak.",
          ],
        },
        es: {
          title: "Tu perfil",
          summary: "Lo que ven los clientes, lo que revisa una persona antes de publicarlo y cómo hacerlo bien a la primera.",
          sections: [
            {
              heading: "Por qué importa",
              paragraphs: [
                "Cada cliente tiene un solo Runner principal. Cuando un hogar te elige, tú eres la persona que esperan en la puerta semana tras semana. Un perfil claro y completo es la forma de ganar esos clientes recurrentes y conservarlos.",
              ],
            },
            {
              heading: "Tu foto",
              paragraphs: [
                "Tu foto pública de perfil la revisa una persona antes de publicarse. Cada cambio también se revisa, y la foto anterior se mantiene visible hasta que se aprueba la nueva, así nunca te quedas sin foto.",
              ],
              bullets: [
                "Cabeza y hombros, mirando a la cámara.",
                "Buena luz. La luz natural junto a una ventana funciona bien.",
                "Sin lentes de sol.",
                "Sin otras personas en la imagen.",
                "Sin texto, logotipos, usuarios de redes ni números de teléfono en la imagen.",
              ],
            },
            {
              heading: "Cómo llegas",
              paragraphs: [
                "Tu vehículo se describe como \"cómo llegas\": marca, modelo y color, más una foto opcional. Es para que tus clientes te reconozcan cuando te estaciones. Tu placa nunca se muestra, y Lista de Compras Plus no clasifica a los Runners por su vehículo.",
              ],
            },
            {
              heading: "Idiomas",
              paragraphs: [
                "Los idiomas que hablas se muestran a los clientes. Incluye todos los que uses con comodidad en la puerta. Compartir idioma suele ser la razón por la que un hogar elige a un Runner.",
              ],
            },
          ],
          actions: [
            "Toma una foto nueva que cumpla las cinco reglas y envíala a revisión.",
            "Completa marca, modelo y color, y agrega una foto del vehículo si tienes una buena.",
            "Agrega todos los idiomas que hablas.",
          ],
        },
      },
    },
    {
      slug: "prospect-board-and-invites",
      related: [
        "building-your-book/your-profile",
        "building-your-book/capacity-and-trusted-runner",
        "reporting-a-problem/customer-issue",
      ],
      body: {
        en: {
          title: "The prospect board and invites",
          summary: "Matching runs both ways: customers can request you, and you can invite households in your zones.",
          sections: [
            {
              heading: "Matching is two-way",
              paragraphs: [
                "Customers can find your profile and request you. You can also open the prospect board, browse households in your zones, and invite the ones that fit your week. Neither side is stuck waiting on the other.",
              ],
            },
            {
              heading: "Anonymous by design",
              paragraphs: [
                "The board shows only what you need to decide whether a household fits your route and schedule.",
              ],
              bullets: [
                "Neighborhood, not address.",
                "Delivery window.",
                "Interests, such as dietary preferences or store habits.",
                "No names, addresses, or contact details until both sides confirm.",
                "The address is revealed only at the first run.",
              ],
            },
            {
              heading: "Invites are rate-limited",
              paragraphs: [
                "You get a limited number of invites, so make each one count. Pick households whose neighborhood and window sit naturally along the stops you already make. A small, tight book beats a scattered one.",
              ],
            },
            {
              heading: "After both sides confirm",
              paragraphs: [
                "The household becomes your customer, you become their primary Runner, and their first run appears in Grocery List Plus Dispatch with the address. Show up on time for that first one. It sets the tone for everything after.",
              ],
            },
          ],
          actions: [
            "Open the prospect board and filter to your zones.",
            "Send invites only to households whose window fits a route you already run.",
            "Check your pending requests from customers and answer them within a day.",
          ],
        },
        es: {
          title: "El tablero de prospectos y las invitaciones",
          summary: "El emparejamiento funciona en ambos sentidos: los clientes pueden solicitarte y tú puedes invitar hogares en tus zonas.",
          sections: [
            {
              heading: "El emparejamiento es en dos sentidos",
              paragraphs: [
                "Los clientes pueden encontrar tu perfil y solicitarte. Tú también puedes abrir el tablero de prospectos, ver hogares en tus zonas e invitar a los que encajen con tu semana. Ninguna de las dos partes se queda esperando a la otra.",
              ],
            },
            {
              heading: "Anónimo por diseño",
              paragraphs: [
                "El tablero muestra solo lo que necesitas para decidir si un hogar encaja con tu ruta y tu horario.",
              ],
              bullets: [
                "Vecindario, no dirección.",
                "Ventana de entrega.",
                "Intereses, como preferencias alimentarias o hábitos de compra.",
                "Sin nombres, direcciones ni datos de contacto hasta que ambas partes confirmen.",
                "La dirección se revela solo en la primera entrega.",
              ],
            },
            {
              heading: "Las invitaciones tienen límite",
              paragraphs: [
                "Tienes un número limitado de invitaciones, así que haz que cada una cuente. Elige hogares cuyo vecindario y ventana queden de forma natural en las paradas que ya haces. Una cartera pequeña y compacta rinde más que una dispersa.",
              ],
            },
            {
              heading: "Cuando ambas partes confirman",
              paragraphs: [
                "El hogar se convierte en tu cliente, tú te conviertes en su Runner principal y su primera entrega aparece en Lista de Compras Plus Dispatch con la dirección. Llega puntual a esa primera entrega. Marca el tono de todo lo que sigue.",
              ],
            },
          ],
          actions: [
            "Abre el tablero de prospectos y filtra por tus zonas.",
            "Envía invitaciones solo a hogares cuya ventana encaje con una ruta que ya haces.",
            "Revisa las solicitudes pendientes de clientes y respóndelas en menos de un día.",
          ],
        },
      },
    },
    {
      slug: "capacity-and-trusted-runner",
      related: [
        "building-your-book/your-schedule-is-a-promise",
        "building-your-book/your-profile",
        "building-your-book/prospect-board-and-invites",
        "reporting-a-problem/what-never-counts-against-you",
        "reading-your-pay-statement/the-splits-line-by-line",
      ],
      body: {
        en: {
          title: "Capacity and Trusted Runner",
          summary: "How to set a capacity you can actually keep, and what Trusted Runner status takes.",
          sections: [
            {
              heading: "Capacity is a promise",
              paragraphs: [
                "Capacity is set in deliveries per week, in your schedule. It is the number of runs Grocery List Plus Dispatch will route to you. Treat it as a promise to your customers, not a ceiling to aim at.",
              ],
            },
            {
              heading: "Set it where you are reliable",
              bullets: [
                "Start with a number you can hit in a bad week, not a good one.",
                "Raise it in small steps once you have kept it for a few weeks running.",
                "Lower it before a busy stretch instead of missing runs during it.",
                "Use approved time off for vacations so your customers are covered and your lane pass is $0.",
              ],
            },
            {
              heading: "Trusted Runner",
              paragraphs: [
                "Trusted Runner status is shown on your card. It tells households that you have a track record.",
              ],
              bullets: [
                `${tr.days} days active.`,
                `${tr.deliveries} deliveries completed.`,
                `${tr.satisfactionPct}% customer satisfaction.`,
              ],
            },
            {
              heading: "Keeping it",
              paragraphs: [
                "Satisfaction comes from the basics: on time, right items, a quick note when something changes. A steady capacity you always meet does more for your standing than a big number you sometimes miss.",
              ],
            },
          ],
          actions: [
            "Open your schedule and set capacity to a number you can hold in a bad week.",
            "Look at your active days, deliveries, and satisfaction, and note which of the three you are closest to.",
            "Book any upcoming time off now so Dispatch can plan around it.",
          ],
        },
        es: {
          title: "Capacidad y Trusted Runner",
          summary: "Cómo fijar una capacidad que de verdad puedas cumplir y qué requiere el estatus de Trusted Runner.",
          sections: [
            {
              heading: "La capacidad es una promesa",
              paragraphs: [
                "La capacidad se fija en entregas por semana, en tu horario. Es el número de entregas que Lista de Compras Plus Dispatch te asignará. Trátala como una promesa a tus clientes, no como un tope al que apuntar.",
              ],
            },
            {
              heading: "Fíjala donde seas confiable",
              bullets: [
                "Empieza con un número que puedas cumplir en una semana mala, no en una buena.",
                "Súbelo en pasos pequeños una vez que lo hayas mantenido varias semanas seguidas.",
                "Bájalo antes de una temporada ocupada en lugar de fallar entregas durante ella.",
                "Usa el tiempo libre aprobado para vacaciones, así tus clientes quedan cubiertos y tu lane pass (pase de carril) es $0.",
              ],
            },
            {
              heading: "Trusted Runner",
              paragraphs: [
                "El estatus de Trusted Runner se muestra en tu tarjeta. Le dice a los hogares que tienes un historial comprobado.",
              ],
              bullets: [
                `${tr.days} días activo.`,
                `${tr.deliveries} entregas completadas.`,
                `${tr.satisfactionPct}% de satisfacción de clientes.`,
              ],
            },
            {
              heading: "Cómo conservarlo",
              paragraphs: [
                "La satisfacción viene de lo básico: puntualidad, los productos correctos y un mensaje rápido cuando algo cambia. Una capacidad estable que siempre cumples hace más por tu reputación que un número grande que a veces fallas.",
              ],
            },
          ],
          actions: [
            "Abre tu horario y fija la capacidad en un número que puedas sostener en una semana mala.",
            "Revisa tus días activos, entregas y satisfacción, y anota a cuál de los tres estás más cerca.",
            "Registra ahora cualquier tiempo libre próximo para que Dispatch pueda planear en torno a él.",
          ],
        },
      },
    },
  ],
};
